import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import {
  HiHome,
  HiDocumentReport,
  HiCog,
} from "react-icons/hi";
import { FiUser, FiShield, FiTruck } from 'react-icons/fi';

type SidebarProps = {
  isCollapsed: boolean;
};

type MenuItem = {
  icon: React.ReactNode;
  name: string;
  path: string;
};

type SubMenuItem = {
  icon: React.ReactNode;
  name: string;
  path: string;
};

const SuperAdminSidebar: React.FC<SidebarProps> = ({ isCollapsed }) => {
  const location = useLocation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isDeliveryBoyMenuOpen, setIsDeliveryBoyMenuOpen] = useState(false);

  const menu: MenuItem[] = [
    { icon: <HiHome />, name: "Home", path: "/superadmin/dashboard" },
    { icon: <FiShield />, name: "Manage Users", path: "#" },
    { icon: <FiTruck />, name: "Delivery Boys", path: "#" },
    { icon: <HiDocumentReport />, name: "Reports", path: "/superadmin/reports" },
    { icon: <HiCog />, name: "Settings", path: "/superadmin/settings" },
  ];

  const userSubMenu: SubMenuItem[] = [
    { icon: <FiUser />, name: "User List", path: "/superadmin/users" },
    { icon: <FiUser />, name: "Add User", path: "/superadmin/users" },
  ];

  const deliveryBoySubMenu: SubMenuItem[] = [
    { icon: <FiUser />, name: "All Delivery Boys", path: "/superadmin/delivery-boys/list" },
    { icon: <FiUser />, name: "Add Delivery Boy", path: "/superadmin/delivery-boys/add" },
  ];

  return (
    <aside
      className={`h-screen bg-white dark:bg-slate-800 text-slate-900 dark:text-white transition-[width] duration-300 ease-in-out ${
        isCollapsed ? "w-16" : "w-64"
      }`}
    >
      {/* Sidebar Title */}
      <div className="flex items-center justify-center h-14 border-b border-white dark:border-slate-800 px-2">
        <div className="flex items-center gap-2">
          <h2
            className={`text-lg font-bold text-primary transition-all origin-left duration-300 ${
              isCollapsed
                ? "opacity-0 scale-95 w-0 overflow-hidden"
                : "opacity-100 scale-100 w-auto"
            }`}
          >
            🛡 Super Admin
          </h2>
          {isCollapsed && <span className="text-xl">🛡</span>}
        </div>
      </div>

      <nav className="flex flex-col gap-2 py-2">
        {menu.map((item, i) => {
          const isUserMenu = item.name === "Manage Users";
          const isDeliveryBoyMenu = item.name === "Delivery Boys";
          const isActive = location.pathname === item.path;

          if (isUserMenu) {
            const isSubActive =
              location.pathname.startsWith("/superadmin/users") ||
              location.pathname.startsWith("/superadmin/add-user");

            return (
              <div key={i}>
                <div
                  onClick={() => setIsUserMenuOpen((prev) => !prev)}
                  className={`flex items-center gap-4 px-4 h-12 text-sm font-medium cursor-pointer transition-colors ${
                    isSubActive
                      ? "bg-slate-100 text-primary dark:bg-slate-800 dark:text-white"
                      : "text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                  }`}
                >
                  <span className="text-xl">{item.icon}</span>
                  {!isCollapsed && <span>{item.name}</span>}
                </div>

                {!isCollapsed && (
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isUserMenuOpen ? "max-h-40 opacity-100" : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="ml-10 flex flex-col gap-1 py-2">
                      {userSubMenu.map((sub, idx) => {
                        const isSubActive = location.pathname === sub.path;
                        return (
                          <Link
                            to={sub.path}
                            key={idx}
                            className={`flex items-center gap-2 px-2 py-1 text-sm rounded-md transition-colors ${
                              isSubActive
                                ? "bg-slate-100 text-primary dark:bg-slate-700 dark:text-white"
                                : "text-slate-600 dark:text-slate-400 hover:text-primary"
                            }`}
                          >
                            {sub.icon}
                            <span>{sub.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          }

          if (isDeliveryBoyMenu) {
            const isSubActive =
              location.pathname.startsWith("/superadmin/delivery-boys");

            return (
              <div key={i}>
                <div
                  onClick={() => setIsDeliveryBoyMenuOpen((prev) => !prev)}
                  className={`flex items-center gap-4 px-4 h-12 text-sm font-medium cursor-pointer transition-colors ${
                    isSubActive
                      ? "bg-slate-100 text-primary dark:bg-slate-800 dark:text-white"
                      : "text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                  }`}
                >
                  <span className="text-xl">{item.icon}</span>
                  {!isCollapsed && <span>{item.name}</span>}
                </div>

                {!isCollapsed && (
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isDeliveryBoyMenuOpen ? "max-h-40 opacity-100" : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="ml-10 flex flex-col gap-1 py-2">
                      {deliveryBoySubMenu.map((sub, idx) => {
                        const isSubActive = location.pathname === sub.path;
                        return (
                          <Link
                            to={sub.path}
                            key={idx}
                            className={`flex items-center gap-2 px-2 py-1 text-sm rounded-md transition-colors ${
                              isSubActive
                                ? "bg-slate-100 text-primary dark:bg-slate-700 dark:text-white"
                                : "text-slate-600 dark:text-slate-400 hover:text-primary"
                            }`}
                          >
                            {sub.icon}
                            <span>{sub.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          }

          return (
            <Link
              to={item.path}
              key={i}
              className={`flex items-center gap-4 px-4 h-12 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-slate-100 text-primary dark:bg-slate-800 dark:text-white"
                  : "text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
              }`}
            >
              <span className="text-xl">{item.icon}</span>
              {!isCollapsed && <span>{item.name}</span>}
            </Link>
          );
        })}
      </nav>
    </aside>
  );
};

export default SuperAdminSidebar;
